/**
 * CetusEd Community Portal — Document Viewer
 *
 * Read-only view of a family-visible document with a download option; the
 * actual PDF is served through the server's authenticated download endpoint.
 *
 * Rule 1 (never inline a legal fact): the retired header and download modal
 * cited "IDEA §300.614" and "FERPA §1232g" inline — neither exists in the
 * verified CITATIONS canon, so the legal shapes were removed. The download
 * reminder now states the practical fact (the file may contain protected
 * education records) without asserting an unverified authority. If a citation
 * is wanted on this surface, add a schema/citations.json entry with a
 * verification STATUS and resolve it via citeRef() — never type it here.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDocumentDetail } from '../../api/hooks';
import { LoadingSpinner } from '../../ui/components/LoadingSpinner';
import { Button } from '../../ui/components/Button';
import { Modal } from '../../ui/components/Modal';

export const DocumentViewer: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const { data: document, isLoading, error } = useDocumentDetail(documentId || '');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadConfirmed, setDownloadConfirmed] = useState(false);

  if (isLoading) return <LoadingSpinner />;

  if (error || !document) {
    return (
      <div className="error-page">
        <h1>Document Not Found</h1>
        <p>The document could not be loaded. It may have been removed or you may not have access.</p>
        <Link to="/documents">Back to Documents</Link>
      </div>
    );
  }

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleConfirmDownload = () => {
    setDownloadConfirmed(true);
    window.open(document.downloadUrl, '_blank');
    setShowDownloadModal(false);
  };

  return (
    <div className="document-viewer-page">
      <nav className="breadcrumb">
        <Link to="/documents">Documents</Link> / {document.title}
      </nav>

      <div className="document-viewer-header">
        <div>
          <h1>{document.title}</h1>
          <p className="document-meta">
            <span className={`document-type-badge document-type--${document.documentType}`}>
              {document.documentType.toUpperCase()}
            </span>
            <span className={`document-status document-status--${document.status}`}>
              {document.status}
            </span>
            <span className="document-date">
              Last updated: {new Date(document.updatedAt).toLocaleDateString()}
            </span>
          </p>
        </div>

        <div className="document-actions">
          <Button onClick={handleDownload} variant="secondary">
            Download Copy
          </Button>
          {document.documentType === 'consent' && document.status === 'draft' && (
            <Link to={`/documents/${document.id}/consent`}>
              <Button>Review & Sign</Button>
            </Link>
          )}
        </div>
      </div>

      <div className="document-viewer-content">
        <p className="document-preview-note">
          Document content rendered securely from the server. This is a read-only view.
        </p>
      </div>

      <Modal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        title="Download Document"
      >
        {downloadConfirmed ? (
          <p>Your download has started. If it does not, please check your browser's download settings.</p>
        ) : (
          <>
            <p>
              You are about to download <strong>{document.title}</strong>. This document may contain
              your student's protected education records.
            </p>
            <p>
              Please keep downloaded documents secure and do not share them with unauthorized individuals.
            </p>
            <div className="modal-actions">
              <Button onClick={() => setShowDownloadModal(false)} variant="ghost">
                Cancel
              </Button>
              <Button onClick={handleConfirmDownload} variant="primary">
                Confirm Download
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};
